import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  cycleSuffix,
  propertyTypeSummary,
  summariseOccupancy,
  type PropertyType,
  type RentCycle,
  type UnitStatus,
  termSuffix,
  toTerm,
} from "@rentorng/core";
import { PropertyTypeMarks, PropertyTypeTag } from "@/components/property-icons";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { useRecordLock } from "@/lib/gating";
import { Padlock, PadlockButton, Refusal } from "@/components/padlock";
import { MeterBar } from "@/components/chart";
import { UnitStatusBadge } from "@/components/status";
import { Button, Card, CardTitle, Detail, EmptyState, ErrorState, LinkButton, PageHeader, Spinner, StatTile, Table } from "@/components/ui";
import { UnitGrid, ViewSwitch, type UnitCardRow } from "@/components/unit-card";
import { PinButton } from "@/components/pin-button";

interface PropertyResponse {
  property: Record<string, string | number | null>;
  landlordName: string | null;
  landlordPhone: string | null;
  managerName: string | null;
  today?: string;
  units: Array<{
    unit: {
      id: string;
      label: string;
      status: string;
      bedrooms: number | null;
      bathrooms: number | null;
      toilets: number | null;
      sizeSqm: number | null;
      listed: boolean | null;
      offers: string[] | null;
      vacantSince: string | null;
      rentAmount: number | null;
      rentCycle: string;
      rentCycleEvery?: number;
      pinnedAt?: string | null;
    };
    /** The first photograph of this unit, when it has one. */
    photoId: string | null;
    pinnedAt?: string | null;
    /** Where the unit is on the public site, when it is on the market at all. */
    publicSlug: string | null;
    leaseId: string | null;
    leaseEnd: string | null;
    tenantName: string | null;
    tenantId: string | null;
    ownerId: string | null;
    ownerName: string | null;
    /** True when this unit belongs to someone other than the building's landlord. */
    ownerIsOwnLandlord: boolean;
  }>;
  expenses: Array<{ id: string; incurredOn: string; category: string; description: string; amount: number }>;
}

export function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { vocab, can } = useSession();
  const fmt = useFormat();

  /*
    Whether this building is above what the plan opens.

    The page still opens and everything on it is still true — the units, the occupancy, the rent
    being collected. What the padlock marks is that the things somebody would *do* to this building
    are shut: editing it, adding units to it, archiving it. Nothing about its money stops, which is
    the sentence the padlock's own modal says in the server's words.
  */
  const lock = useRecordLock("properties");
  const locked = lock.isLocked(String(id));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["property", id],
    queryFn: () => api.get<PropertyResponse>(`/properties/${id}`),
    enabled: Boolean(id),
  });

  const remove = useApiMutation({
    mutationFn: () => api.delete(`/properties/${id}`),
    invalidates: [["properties"], ["dashboard"]],
    onSuccess: () => navigate("/app/properties"),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const property = data.property;
  const occupancy = summariseOccupancy(data.units.map((row) => ({ status: row.unit.status as UnitStatus })));

  /**
   * The property's units, in the shape the shared card reads.
   *
   * The card is the same one the units screen draws, so a unit looks the same wherever it is seen —
   * the photograph it leads with, the status, whether the public site can see it. The building's
   * name is left off: on its own page every card would repeat it.
   */
  const cards: UnitCardRow[] = data.units.map((row) => ({
    id: row.unit.id,
    label: row.unit.label,
    status: row.unit.status,
    bedrooms: row.unit.bedrooms,
    bathrooms: row.unit.bathrooms,
    toilets: row.unit.toilets,
    sizeSqm: row.unit.sizeSqm,
    listed: row.unit.listed,
    offers: row.unit.offers,
    rentAmount: row.unit.rentAmount,
    rentCycle: row.unit.rentCycle,
    rentCycleEvery: row.unit.rentCycleEvery,
    vacantSince: row.unit.vacantSince,
    propertyId: id ?? "",
    propertyName: property.name as string,
    propertyTypes: (property.types as string[] | null) ?? [],
    ownerId: row.ownerId,
    ownerName: row.ownerName,
    ownerIsOwnLandlord: row.ownerIsOwnLandlord,
    tenantName: row.tenantName,
    leaseId: row.leaseId,
    leaseEnd: row.leaseEnd,
    photoId: row.photoId,
    publicSlug: row.publicSlug,
    pinnedAt: row.unit.pinnedAt,
  }));

  /**
   * A page at a time, in the browser.
   *
   * A block of forty flats is forty cards with a picture on each — worth loading in pages even
   * though the property's payload already holds them all. Twelve fills three rows on a laptop and
   * two on a phone; the control disappears entirely when there is nothing to page through.
   */
  const PER_PAGE = 12;
  const page = Math.max(1, Number(search.get("unitPage") ?? 1) || 1);
  const pages = Math.max(1, Math.ceil(cards.length / PER_PAGE));
  const shown = cards.slice((Math.min(page, pages) - 1) * PER_PAGE, Math.min(page, pages) * PER_PAGE);
  const goToPage = (next: number) => {
    const then = new URLSearchParams(search);
    if (next <= 1) then.delete("unitPage");
    else then.set("unitPage", String(next));
    setSearch(then, { replace: true });
  };

  /** Grid or list, kept in the address so a link carries the choice. See the units screen. */
  const view = search.get("view") === "list" ? "list" : "grid";
  const setView = (next: "grid" | "list") => {
    const then = new URLSearchParams(search);
    if (next === "grid") then.delete("view");
    else then.set("view", next);
    setSearch(then, { replace: true });
  };
  const contractedRent = data.units.reduce((sum, row) => sum + (row.leaseId ? (row.unit.rentAmount ?? 0) : 0), 0);
  /** Everyone who owns a unit here other than the landlord on the building record. */
  const otherOwners = Array.from(
    new Set(
      data.units
        .filter((row) => row.ownerIsOwnLandlord && row.ownerId)
        .map((row) => row.ownerId as string),
    ),
  );

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <PropertyTypeMarks types={(property.types as unknown as string[]) ?? []} size={24} />
            <span className="truncate">{String(property.name)}</span>
            {locked ? <Padlock gate={lock.gate} label="Locked" /> : null}
          </span>
        }
        back={{ to: "/app/properties", label: titleCase(vocab.properties) }}
        subtitle={
          <span>
            {propertyTypeSummary((property.types as unknown as string[]) ?? [])}
            {property.addressLine ? ` · ${property.addressLine}` : ""}
            {property.city ? `, ${property.city}` : ""}
          </span>
        }
        actions={
          can("property.edit") ? (
            locked ? (
              /* Every way of changing this building, drawn shut rather than taken away. The pin
                 goes with them: pinning is a write the API refuses on a padlocked record, and a
                 control that answers with a refusal is worse than one that says why first. */
              <>
                <PadlockButton gate={lock.gate}>Add {vocab.units}</PadlockButton>
                <PadlockButton gate={lock.gate}>Edit</PadlockButton>
              </>
            ) : (
              <>
                {/* First, because "keep this in front of me" is the thing somebody does while
                    reading the page rather than on the way out of it. */}
                <PinButton
                  kind="property"
                  id={String(id)}
                  pinnedAt={property.pinnedAt as string | null}
                  size="md"
                  invalidates={[["properties"], ["property", String(id)]]}
                />
                <LinkButton to={`/app/properties/${id}/units/new`} variant="secondary" need="property.edit">
                  Add {vocab.units}
                </LinkButton>
                <LinkButton to={`/app/properties/${id}/edit`} variant="secondary" need="property.edit">
                  Edit
                </LinkButton>
              </>
            )
          ) : null
        }
      />

      {remove.error ? (
        <div className="mb-4">
          <Refusal error={remove.error} message={remove.errorMessage} />
        </div>
      ) : null}

      {/*
        The list gets the room, the panels get what they need.
        Thirds gave the side column a third of a wide screen for six short lines of text, while the
        unit list — the reason anybody opens this page — was squeezed into two. A fixed 22rem rail
        is as much as the panels ever use, and everything else goes to the list.
      */}
      {/*
        The shape of the building, above what is in it — the same tiles the dashboard uses, in place
        of the panel that used to sit in the rail. A figure somebody glances at belongs where the
        eye lands first, not beside the sixth line of an address.
      */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Occupied" value={occupancy.occupied} sub={`of ${occupancy.total}`} tone="success" />
        <StatTile label="Vacant" value={occupancy.vacant} tone={occupancy.vacant ? "warn" : "muted"} />
        <StatTile label="On notice" value={occupancy.notice} tone={occupancy.notice ? "warn" : "muted"} />
        <StatTile label="Contracted rent" value={fmt.money(contractedRent)} sub="across live tenancies" tone="brand" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          <Card padded={false}>
            <div className="p-4 sm:p-5">
              {/* The switch belongs on the header's own line, at the right, with whatever else
                  this card offers — under the heading it read as the first thing in the list. */}
              <CardTitle
                hint={`${occupancy.occupied} of ${occupancy.total} let`}
                action={
                  <span className="flex items-center gap-3">
                    <ViewSwitch view={view} onChange={setView} />
                    {can("property.edit") ? (
                      <Link to={`/app/properties/${id}/units/new`} className="link">
                        Add {vocab.units}
                      </Link>
                    ) : null}
                  </span>
                }
              >
                {titleCase(vocab.units)}
              </CardTitle>
            </div>

            {data.units.length === 0 ? (
              <EmptyState
                title={`No ${vocab.units} yet.`}
                hint="Add them one at a time, or create a numbered run — “Flat 1” to “Flat 24” — in one go."
                action={<LinkButton to={`/app/properties/${id}/units/new`} need="property.edit">Add {vocab.units}</LinkButton>}
              />
            ) : (
              <div className="p-4 sm:p-5 sm:pt-0">
                {view === "grid" ? (
                  <UnitGrid units={shown} showProperty={false} today={data.today ?? ""} />
                ) : (
                <Table
                  head={
                    <tr>
                      <th>{titleCase(vocab.unit)}</th>
                      <th>{titleCase(vocab.landlord)}</th>
                      <th>Status</th>
                      <th>{titleCase(vocab.tenant)}</th>
                      <th>Ends</th>
                      <th className="num">{titleCase(vocab.rent)}</th>
                    </tr>
                  }
                >
                  {data.units.map((row) => (
                    <tr key={row.unit.id}>
                      <td>
                        <Link to={`/app/units/${row.unit.id}/edit`} className="link">
                          {row.unit.label}
                        </Link>
                        {row.unit.bedrooms ? (
                          <span className="ml-2 text-xs text-ink-400">
                            {row.unit.bedrooms} bed{row.unit.bathrooms ? ` · ${row.unit.bathrooms} bath` : ""}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {row.ownerId ? (
                          <Link
                            to={`/app/clients/${row.ownerId}`}
                            className={row.ownerIsOwnLandlord ? "link font-medium" : "text-ink-500 hover:underline"}
                          >
                            {row.ownerName}
                          </Link>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td>
                        <UnitStatusBadge status={row.unit.status as UnitStatus} />
                      </td>
                      <td>
                        {row.tenantId ? (
                          <Link to={`/app/clients/${row.tenantId}`} className="link">
                            {row.tenantName}
                          </Link>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="text-ink-500">
                        {row.leaseId ? (
                          <Link to={`/app/tenancies/${row.leaseId}`} className="hover:underline">
                            {fmt.date(row.leaseEnd)}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="num tabular">
                        {row.unit.rentAmount ? (
                          <>
                            {fmt.money(row.unit.rentAmount)}
                            <span className="block text-xs text-ink-400">{termSuffix(toTerm(row.unit.rentCycle as RentCycle, row.unit.rentCycleEvery))}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
                )}

                {pages > 1 && view === "grid" ? (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 pt-3 text-sm">
                    <p className="text-ink-500">
                      {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, cards.length)} of {cards.length} {cards.length === 1 ? vocab.unit : vocab.units}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                        Previous
                      </Button>
                      <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => goToPage(page + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </Card>

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle
                hint={`Deducted from the ${vocab.landlord}'s remittance when recoverable.`}
                action={<Link to="/app/expenses" className="link">All expenses</Link>}
              >
                Recent expenses
              </CardTitle>
            </div>
            {data.expenses.length === 0 ? (
              <EmptyState title={`No expenses recorded against this ${vocab.property}.`} />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="num">Amount</th>
                  </tr>
                }
              >
                {data.expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="text-ink-500">{fmt.date(expense.incurredOn)}</td>
                    <td>{expense.category}</td>
                    <td className="text-ink-500">{expense.description}</td>
                    <td className="num tabular">{fmt.money(expense.amount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardTitle>Details</CardTitle>
            <dl>
              <Detail label={titleCase(vocab.landlord)}>
                {property.landlordPartyId ? (
                  <Link to={`/app/clients/${property.landlordPartyId}`} className="link">
                    {data.landlordName}
                  </Link>
                ) : (
                  <span className="text-ink-400">Not set</span>
                )}
                {/* Naming only the building owner would be a half-truth once units are sold
                    off individually, so the count of other owners is stated plainly. */}
                {otherOwners.length ? (
                  <span className="block text-xs text-ink-500">
                    and {otherOwners.length} other {otherOwners.length === 1 ? vocab.landlord : `${vocab.landlord}s`} across
                    the {vocab.units}
                  </span>
                ) : null}
              </Detail>
              {/* A commission rate was drawn here. Nothing is taken off a landlord's rent any
                  more, so there is no rate to state — ADR-0131. */}
              <Detail label="Managed by">{data.managerName ?? <span className="text-ink-400">Anyone</span>}</Detail>
              <Detail label="Type">
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {((property.types as unknown as string[]) ?? []).map((type) => (
                    <PropertyTypeTag key={type} type={type} />
                  ))}
                </span>
              </Detail>
              <Detail label="Address">{property.addressLine ?? "—"}</Detail>
              <Detail label="City">{property.city ?? "—"}</Detail>
            </dl>
            {property.notes ? <p className="mt-3 whitespace-pre-wrap text-sm text-ink-600">{property.notes}</p> : null}
          </Card>



          {can("property.delete") ? (
            <Card>
              <CardTitle hint={`A ${vocab.property} with a live ${vocab.lease} cannot be removed.`}>Danger zone</CardTitle>
              {locked ? (
                /* Shut before the press rather than after it. The API refuses this one on a
                   padlocked record, and the padlock is asked first — before the live-tenancy
                   guard — so the refusal would arrive here anyway; saying it in advance is the
                   difference between an offer and a trap. Archiving is untouched and is still the
                   way back under the allowance. */
                <PadlockButton gate={lock.gate}>Remove this {vocab.property}</PadlockButton>
              ) : (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove ${property.name}? Its ${vocab.units} are archived with it.`)) remove.mutate(undefined as never);
                  }}
                >
                  Remove this {vocab.property}
                </Button>
              )}
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
